import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { useFilters } from "../context/FilterContext";
import { appendFyToSearchParams, buildDashboardParams, DASHBOARD_FY_OPTIONS } from "../utils/dashboardFilters";

const filterTrendByDropdown = (rows = []) =>
  rows.filter((row) => row?.fy && DASHBOARD_FY_OPTIONS.includes(String(row.fy).trim()));

/**
 * Custom hook that encapsulates all data-fetching, state management,
 * and map drill-down logic for the CommandCentre page.
 */
export function useCommandCentreData() {
  const { filters, filterOptions, viewMode, setGlobalLoading, API } = useFilters();
  const grantsTableRef = useRef(null);

  // ── Core dashboard state ──────────────────────────────────────────
  const [summary, setSummary] = useState(null);
  const [geoSummary, setGeoSummary] = useState(null);
  const [stateData, setStateData] = useState([]);
  const [allocationByTheme, setAllocationByTheme] = useState([]);
  const [disbTrend, setDisbTrend] = useState([]);
  const [ragSummary, setRagSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Map drill state ───────────────────────────────────────────────
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [districtData, setDistrictData] = useState([]);
  const [blockData, setBlockData] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);

  // ── Region-filtered summary (right panel) ─────────────────────────
  const [regionSummary, setRegionSummary] = useState(null);
  const [regionGeoSummary, setRegionGeoSummary] = useState(null);
  const [regionRagSummary, setRegionRagSummary] = useState(null);

  // ── Aspirational data ─────────────────────────────────────────────
  const [aspirationalData, setAspirationalData] = useState({ ad: null, ab: null });

  // ── YoY comparison data ─────────────────────────────────────────
  const [yoyData, setYoyData] = useState(null);

  // ── Heatmap data ──────────────────────────────────────────────────
  const [trustMatrix, setTrustMatrix] = useState({ data: [], trusts: [], themes: [] });
  const [awpMatrix, setAwpMatrix] = useState({ data: [], trusts: [], themes: [] });
  const [heatmapToggle, setHeatmapToggle] = useState("grants");

  // ── Sankey data ────────────────────────────────────────────────────
  const [sankeyData, setSankeyData] = useState(null);

  // ── Quarterly data ────────────────────────────────────────────────
  const [quarterlyData, setQuarterlyData] = useState([]);

  // ── Map view toggle ───────────────────────────────────────────────
  const [mapViewMode, setMapViewMode] = useState("states");
  const [allDistrictData, setAllDistrictData] = useState([]);

  // ── Grants table state ────────────────────────────────────────────
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [grantsPage, setGrantsPage] = useState(1);
  const [grantsTotal, setGrantsTotal] = useState(0);
  const [grantsTotalPages, setGrantsTotalPages] = useState(1);
  const [grantsSearch, setGrantsSearch] = useState("");
  const [grantsSearchInput, setGrantsSearchInput] = useState("");
  const [grantsSortBy, setGrantsSortBy] = useState("grant_approved_date");
  const [grantsSortOrder, setGrantsSortOrder] = useState("desc");
  const [grantsExporting, setGrantsExporting] = useState(false);
  const [grantsTableOpen, setGrantsTableOpen] = useState(false);

  // ── Shared params ─────────────────────────────────────────────────
  const params = buildDashboardParams(filters, { view_mode: viewMode });
  const trendParams = { ...params };
  delete trendParams.fy;


  // ── Clear grants search on filter/geography change ────────────────
  useEffect(() => {
    setGrantsSearch("");
    setGrantsSearchInput("");
    setGrantsPage(1);
  }, [filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode, selectedState, selectedDistrict]);

  // ── Fetch charts scoped to a geography ────────────────────────────
  const fetchChartsForGeo = useCallback((geoState, geoDistrict) => {
    const chartParams = { ...params, state: geoState || undefined, district: geoDistrict || undefined };
    const geoTrendParams = { ...trendParams, state: geoState || undefined, district: geoDistrict || undefined };
    Promise.all([
      axios.get(`${API}/executive/allocation-by-theme`, { params: chartParams }),
      axios.get(`${API}/executive/disbursement-trend`, { params: geoTrendParams }),
      axios.get(`${API}/executive/trust-theme-matrix`, { params: chartParams }).catch(() => ({ data: { data: [], trusts: [], themes: [] } })),
      axios.get(`${API}/executive/quarterly`, { params: chartParams }).catch(() => ({ data: [] })),
    ]).then(([themeRes, trendRes, matrixRes, qtrRes]) => {
      setAllocationByTheme(themeRes.data);
      setDisbTrend(filterTrendByDropdown(trendRes.data));
      setTrustMatrix(matrixRes.data);
      setQuarterlyData(qtrRes.data);
    }).catch(console.error);
  }, [params, trendParams, API]);

  // ── Main data fetch — resets drill-down on filter change ──────────
  useEffect(() => {
    setSelectedState(null);
    setSelectedDistrict(null);
    setRegionSummary(null);
    setRegionGeoSummary(null);
    setRegionRagSummary(null);
    setLoading(true);
    setGlobalLoading(true);
    Promise.all([
      axios.get(`${API}/executive/summary`, { params }),
      axios.get(`${API}/geographic/summary`, { params }),
      axios.get(`${API}/geographic/state-data`, { params }),
      axios.get(`${API}/executive/allocation-by-theme`, { params }),
      axios.get(`${API}/executive/disbursement-trend`, { params: trendParams }),
      axios.get(`${API}/executive/rag-summary`, { params }).catch(() => ({ data: {} })),
      axios.get(`${API}/geographic/aspirational-districts`, { params }).catch(() => ({ data: {} })),
      axios.get(`${API}/geographic/aspirational-blocks`, { params }).catch(() => ({ data: {} })),
      axios.get(`${API}/executive/trust-theme-matrix`, { params }).catch(() => ({ data: { data: [], trusts: [], themes: [] } })),
      axios.get(`${API}/geographic/all-district-data`, { params }).catch(() => ({ data: [] })),
      axios.get(`${API}/executive/yoy-comparison`, { params }).catch(() => ({ data: null })),
      axios.get(`${API}/executive/trust-theme-awp`, { params: { fy: params.fy } }).catch(() => ({ data: { data: [], trusts: [], themes: [] } })),
      axios.get(`${API}/executive/sankey`, { params }).catch(() => ({ data: null })),
      axios.get(`${API}/executive/quarterly`, { params }).catch(() => ({ data: [] })),
    ]).then(([sumRes, geoRes, stateRes, themeRes, trendRes, ragRes, adRes, abRes, matrixRes, allDistRes, yoyRes, awpRes, sankeyRes, qtrRes]) => {
      setSummary(sumRes.data);
      setGeoSummary(geoRes.data);
      setStateData(stateRes.data?.data || []);
      setAllocationByTheme(themeRes.data);
      setDisbTrend(filterTrendByDropdown(trendRes.data));
      setRagSummary(ragRes.data);
      setAspirationalData({ ad: adRes.data, ab: abRes.data });
      setTrustMatrix(matrixRes.data);
      setAllDistrictData(allDistRes.data || []);
      setYoyData(yoyRes.data);
      setAwpMatrix(awpRes.data);
      setSankeyData(sankeyRes.data);
      setQuarterlyData(Array.isArray(qtrRes.data) ? qtrRes.data : []);
    }).catch(console.error).finally(() => { setLoading(false); setGlobalLoading(false); });
  }, [filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode, API]);

  // ── Grants table params ───────────────────────────────────────────
  const grantsParams = useMemo(() => ({
    ...buildDashboardParams(filters, { view_mode: viewMode }),
    grantee: filters.grantee || undefined,
    state: selectedState || undefined, district: selectedDistrict || undefined,
    search: grantsSearch || undefined,
    page: grantsPage, page_size: 50,
    sort_by: grantsSortBy, sort_order: grantsSortOrder,
  }), [filters.fy, viewMode, filters.theme, filters.trust, filters.status, filters.grantee,
       selectedState, selectedDistrict, grantsSearch, grantsPage, grantsSortBy, grantsSortOrder]);

  // ── Fetch grants table ────────────────────────────────────────────
  useEffect(() => {
    setGrantsLoading(true);
    axios.get(`${API}/grants/list`, { params: grantsParams })
      .then(res => {
        setGrants(res.data.data || []);
        setGrantsTotal(res.data.total || 0);
        setGrantsTotalPages(res.data.total_pages || 1);
      }).catch(console.error).finally(() => setGrantsLoading(false));
  }, [grantsParams, API]);

  // ── Grants table handlers ─────────────────────────────────────────
  const handleGrantsSort = useCallback((field) => {
    if (grantsSortBy === field) {
      setGrantsSortOrder(o => o === "asc" ? "desc" : "asc");
    } else {
      setGrantsSortBy(field);
      setGrantsSortOrder("desc");
    }
    setGrantsPage(1);
  }, [grantsSortBy]);

  const handleGrantsSearch = (e) => { e.preventDefault(); setGrantsSearch(grantsSearchInput); setGrantsPage(1); };
  const clearGrantsSearch = () => { setGrantsSearchInput(""); setGrantsSearch(""); setGrantsPage(1); };

  const handleGrantsExport = async () => {
    setGrantsExporting(true);
    try {
      const ep = new URLSearchParams();
      appendFyToSearchParams(ep, filters.fy);
      if (viewMode) ep.append("view_mode", viewMode);
      if (filters.theme) ep.append("theme", filters.theme);
      if (filters.trust) ep.append("trust", filters.trust);
      if (filters.status) ep.append("status", filters.status);
      if (filters.grantee) ep.append("grantee", filters.grantee);
      if (selectedState) ep.append("state", selectedState);
      if (selectedDistrict) ep.append("district", selectedDistrict);
      if (grantsSearch) ep.append("search", grantsSearch);
      const res = await axios.get(`${API}/grants/export?${ep.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `grants_export_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Export failed:", err); }
    finally { setGrantsExporting(false); }
  };

  const scrollToGrantsTable = () => {
    setGrantsTableOpen(true);
    setTimeout(() => {
      grantsTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // ── Map drill handlers ────────────────────────────────────────────
  const handleStateSelect = (stateName) => {
    if (stateName === null) {
      setSelectedState(null);
      setSelectedDistrict(null);
      setDistrictData([]);
      setBlockData([]);
      setRegionSummary(null);
      setGrantsPage(1);
      setRegionGeoSummary(null);
      setRegionRagSummary(null);
      fetchChartsForGeo(null, null);
      Promise.all([
        axios.get(`${API}/geographic/aspirational-districts`, { params }).catch(() => ({ data: {} })),
        axios.get(`${API}/geographic/aspirational-blocks`, { params }).catch(() => ({ data: {} })),
        axios.get(`${API}/executive/yoy-comparison`, { params }).catch(() => ({ data: null })),
        axios.get(`${API}/executive/quarterly`, { params }).catch(() => ({ data: [] })),
      ]).then(([adRes, abRes, yoyRes, qtrRes]) => {
        setAspirationalData({ ad: adRes.data, ab: abRes.data });
        setYoyData(yoyRes.data);
        setQuarterlyData(qtrRes.data);
      });
      return;
    }
    setSelectedState(stateName);
    setSelectedDistrict(null);
    setBlockData([]);
    setMapLoading(true);
    setGrantsPage(1);
    fetchChartsForGeo(stateName, null);
    Promise.all([
      axios.get(`${API}/geographic/district-data`, { params: { ...params, state: stateName } }),
      axios.get(`${API}/executive/summary`, { params: { ...params, state: stateName } }),
      axios.get(`${API}/geographic/summary`, { params: { ...params, state: stateName } }),
      axios.get(`${API}/executive/rag-summary`, { params: { ...params, state: stateName } }),
      axios.get(`${API}/geographic/aspirational-districts`, { params: { ...params, state: stateName } }).catch(() => ({ data: {} })),
      axios.get(`${API}/geographic/aspirational-blocks`, { params: { ...params, state: stateName } }).catch(() => ({ data: {} })),
      axios.get(`${API}/executive/yoy-comparison`, { params: { ...params, state: stateName } }).catch(() => ({ data: null })),
      axios.get(`${API}/executive/quarterly`, { params: { ...params, state: stateName } }).catch(() => ({ data: [] })),
    ]).then(([distRes, sumRes, geoRes, ragRes, adRes, abRes, yoyRes, qtrRes]) => {
      setDistrictData(distRes.data?.data || distRes.data || []);
      setRegionSummary(sumRes.data);
      setRegionGeoSummary(geoRes.data);
      setRegionRagSummary(ragRes.data);
      setAspirationalData({ ad: adRes.data, ab: abRes.data });
      setYoyData(yoyRes.data);
      setQuarterlyData(qtrRes.data);
    }).finally(() => setMapLoading(false));
  };

  const handleDistrictSelect = (districtName, shapeID) => {
    if (districtName === null) {
      setSelectedDistrict(null);
      setBlockData([]);
      setMapLoading(true);
      fetchChartsForGeo(selectedState, null);
      Promise.all([
        axios.get(`${API}/executive/summary`, { params: { ...params, state: selectedState } }),
        axios.get(`${API}/geographic/summary`, { params: { ...params, state: selectedState } }),
        axios.get(`${API}/executive/rag-summary`, { params: { ...params, state: selectedState } }),
        axios.get(`${API}/geographic/aspirational-districts`, { params: { ...params, state: selectedState } }).catch(() => ({ data: {} })),
        axios.get(`${API}/geographic/aspirational-blocks`, { params: { ...params, state: selectedState } }).catch(() => ({ data: {} })),
        axios.get(`${API}/executive/yoy-comparison`, { params: { ...params, state: selectedState } }).catch(() => ({ data: null })),
        axios.get(`${API}/executive/quarterly`, { params: { ...params, state: selectedState } }).catch(() => ({ data: [] })),
      ]).then(([sumRes, geoRes, ragRes, adRes, abRes, yoyRes, qtrRes]) => {
        setRegionSummary(sumRes.data);
        setRegionGeoSummary(geoRes.data);
        setRegionRagSummary(ragRes.data);
        setAspirationalData({ ad: adRes.data, ab: abRes.data });
        setYoyData(yoyRes.data);
        setQuarterlyData(qtrRes.data);
      }).finally(() => setMapLoading(false));
      return;
    }
    setSelectedDistrict(districtName);
    setMapLoading(true);
    fetchChartsForGeo(selectedState, districtName);
    Promise.all([
      axios.get(`${API}/geographic/block-data`, { params: { ...params, state: selectedState, district: districtName } }),
      axios.get(`${API}/executive/summary`, { params: { ...params, state: selectedState, district: districtName } }),
      axios.get(`${API}/geographic/summary`, { params: { ...params, state: selectedState, district: districtName } }),
      axios.get(`${API}/executive/rag-summary`, { params: { ...params, state: selectedState, district: districtName } }),
      axios.get(`${API}/geographic/aspirational-districts`, { params: { ...params, state: selectedState, district: districtName } }).catch(() => ({ data: {} })),
      axios.get(`${API}/geographic/aspirational-blocks`, { params: { ...params, state: selectedState, district: districtName } }).catch(() => ({ data: {} })),
      axios.get(`${API}/executive/yoy-comparison`, { params: { ...params, state: selectedState, district: districtName } }).catch(() => ({ data: null })),
      axios.get(`${API}/executive/quarterly`, { params: { ...params, state: selectedState, district: districtName } }).catch(() => ({ data: [] })),
    ]).then(([blockRes, sumRes, geoRes, ragRes, adRes, abRes, yoyRes, qtrRes]) => {
      setBlockData(blockRes.data?.data || blockRes.data || []);
      setRegionSummary(sumRes.data);
      setRegionGeoSummary(geoRes.data);
      setRegionRagSummary(ragRes.data);
      setAspirationalData({ ad: adRes.data, ab: abRes.data });
      setYoyData(yoyRes.data);
      setQuarterlyData(qtrRes.data);
    }).finally(() => setMapLoading(false));
  };

  // ── Derived values ────────────────────────────────────────────────
  const sf = filterOptions.data_sources?.grants || filterOptions.data_available;
  const s = regionSummary || summary;
  const hasGrants = s && (s.total_grants > 0);
  const g = regionGeoSummary || geoSummary;
  const totalSanctioned = allocationByTheme.reduce(
    (sum, d) => sum + (sf ? (d.allocation_cr || 0) : (d.disbursed_cr || 0)), 0
  );

  return {
    // Core data
    summary, geoSummary, stateData, allocationByTheme, disbTrend,
    ragSummary, loading, sf, s, hasGrants, g, totalSanctioned,
    // Map drill
    selectedState, selectedDistrict, districtData, blockData, mapLoading,
    handleStateSelect, handleDistrictSelect,
    // Region panel
    regionSummary, regionGeoSummary, regionRagSummary,
    // Aspirational
    aspirationalData,
    // YoY comparison
    yoyData,
    // Heatmap
    trustMatrix, awpMatrix, heatmapToggle, setHeatmapToggle,
    // Sankey
    sankeyData,
    // Quarterly
    quarterlyData,
    // Map view
    mapViewMode, setMapViewMode, allDistrictData,
    // Grants table
    grants, grantsLoading, grantsPage, setGrantsPage, grantsTotal,
    grantsTotalPages, grantsSearchInput, setGrantsSearchInput,
    grantsSortBy, grantsSortOrder, grantsExporting,
    handleGrantsSort, handleGrantsSearch, clearGrantsSearch,
    handleGrantsExport, scrollToGrantsTable, grantsTableRef,
    grantsTableOpen, setGrantsTableOpen,
    // Context
    filters, filterOptions, viewMode, API,
  };
}
